<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import DropZone from "~/components/Common/Upload/DropZone.vue";
import { CopilotDocTypeEnum } from "~/utils/enums/copilot/chat.enums";
import CopilotUrlInput from "./CopilotUrlInput.vue";

const props = defineProps<{
  programUrl: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  "update:programUrl": [value: string];
  /** The type is known when the picker is opened for one, unknown for a drop or a plain browse. */
  file: [file: File, docType?: CopilotDocTypeEnum];
  "analyze-url": [];
}>();

const fileInputRef = ref<HTMLInputElement | null>(null);
/** Type the open picker was asked for — the file it returns is of that type. */
const pickedDocType = ref<CopilotDocTypeEnum | null>(null);

/*
 * Left untyped by the zone itself since the design dropped the per-type shortcuts: the copy now
 * promises the assistant reads the PDF and works out what it is. Still typed for the error recovery,
 * which reopens the picker for a program after a URL the assistant could not fetch.
 */
function openFilePicker(docType?: CopilotDocTypeEnum) {
  if (props.disabled) return;
  pickedDocType.value = docType ?? null;
  fileInputRef.value?.click();
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (file) emit("file", file, pickedDocType.value ?? undefined);
  pickedDocType.value = null;
  input.value = "";
}

function onFilesDropped(event: DragEvent) {
  if (props.disabled) return;

  const file = (event.target as HTMLInputElement)?.files?.[0];
  if (file) emit("file", file);
}

defineExpose({ openFilePicker });
</script>

<template>
  <DropZone @files-dropped="onFilesDropped">
    <template #default="{ dropZoneActive }">
      <!-- Spacings are the design's own spacers (16 / 6 / 12), so the column carries no shared gap. -->
      <div
        class="flex flex-col items-center rounded-[12px] border border-dashed px-[26px] py-6 transition-colors"
        :class="dropZoneActive ? 'border-primary-blue-color bg-light-blue-color' : 'border-stroke-grey-color'"
      >
        <!--
          The invitation is the browse affordance: with the per-type shortcuts gone, nothing else in
          the zone opens the picker, and only a `button` offers that to the keyboard too. It stops at
          the title so the accessible name stays "Déposez un document" — the line under it explains
          what is accepted and is read as the text it is, not folded into the button's name.
        -->
        <button
          type="button"
          class="group flex cursor-pointer flex-col items-center gap-4 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="disabled"
          @click="openFilePicker()"
        >
          <span class="flex h-11 w-11 items-center justify-center rounded-full bg-light-blue-color">
            <SvgIcon name="upload" class="h-5 w-5 text-primary-blue-color" />
          </span>

          <span class="heading_4_bold text-primary-color transition-colors group-hover:text-primary-blue-color">
            Déposez un document
          </span>
        </button>

        <span class="small_regular mt-1.5 text-center text-secondary-color">
          Programme de formation ou feuille d’émargement en pdf. J’analyse et pré-remplis les informations pour vous.
        </span>

        <CopilotUrlInput
          class="mt-3"
          :model-value="programUrl"
          :disabled="disabled"
          @update:model-value="emit('update:programUrl', $event)"
          @submit="emit('analyze-url')"
        />
      </div>

      <input ref="fileInputRef" type="file" accept="application/pdf" class="hidden" @change="onFileSelected" />
    </template>
  </DropZone>
</template>
